import React, { useRef } from "react";
import { PrinterOutlined } from "@ant-design/icons";
import { App, Button, Card, Typography } from "antd";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";

import SalesReturnGSTR1Filters from "./components/SalesReturnGSTR1Filters";
import SalesReturnGSTR1Table from "./components/SalesReturnGSTR1Table";
import ReportDom from "../../../components/common/ReportDom";

const { Title } = Typography;

const format = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;

const SalesReturnGSTR1 = () => {
  const reportRef = useRef();
  const { data, filters } = useSelector(
    (state) => state.salesReturnGSTR1
  );

  // ✅ SINGLE SOURCE OF TRUTH
  const defaultRows = [
    { key: "b2b", label: "Taxable outward supplies made to registered persons (including UIN-holders)" },
    { key: "b2c_large", label: "Taxable outward inter-State supplies to un-registered persons where the invoice value is more than ₹1 lakh" },
    { key: "zero", label: "Zero rated supplies and Deemed Exports" },
    { key: "consumer", label: "Taxable outward supplies to consumer" },
    { key: "nil", label: "Nil rated, Exempted and non GST outward supplies" },
    { key: "cdn", label: "Details of Credit/Debit Notes and Refund Voucher" },
    { key: "cdn_unreg", label: "Details of Credit/Debit Notes and Refund Voucher (Unregistered)" },
    { key: "adv", label: "Consolidated Statement of Advances Received" },
    { key: "adv_adj", label: "Tax already paid (on advance receipt) on invoices issued in the current period" },
    { key: "hsn_b2b", label: "HSN-wise summary of the B2B Supplies" },
    { key: "hsn_b2c", label: "HSN-wise summary of the B2C Supplies" },
    { key: "ecom", label: "Supplies made through E-Commerce Operators" },
  ];

  // ✅ FAST LOOKUP MAP
  const backendMap = Object.fromEntries(
    (data || []).map((item) => [item.key, item])
  );

  // ✅ FINAL MERGE
  const formattedData = defaultRows.map((row) => {
    const match = backendMap[row.key] || {};

    return {
      ...row,
      igst: match.igst ?? 0,
      cgst: match.cgst ?? 0,
      sgst: match.sgst ?? 0,
      taxable: match.taxable ?? 0,
      total: match.total ?? 0,
    };
  });

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "GSTR-1",
  });

  const columns = [
    { title: "Description", dataIndex: "label" },
    { title: "IGST Amount", dataIndex: "igst", render: format },
    { title: "CGST Amount", dataIndex: "cgst", render: format },
    { title: "SGST Amount", dataIndex: "sgst", render: format },
    { title: "Taxable Amount", dataIndex: "taxable", render: format },
    { title: "Invoice Total", dataIndex: "total", render: format },
  ];

  return (
    <div style={{ padding: 12 }}>
      <App>
        <Card
          title={<Title level={4}>GSTR-1</Title>}
          extra={
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
          }
          style={{ marginBottom: 16 }}
        />

        <SalesReturnGSTR1Filters />

        <Card style={{ marginBottom: 16 }} styles={{ padding: 12 }}>
          <SalesReturnGSTR1Table data={formattedData} />
        </Card>
      </App>

      <div style={{ position: "absolute", top: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          showTotal={false}
          title="Sales Return GSTR-1"
          filters={filters}
          columns={columns}
          data={formattedData}
        />
      </div>
    </div>
  );
};

export default SalesReturnGSTR1;